<template>
  <div class="management-distribution-card">
    <div v-if="hasDisplayTotal" class="management-distribution-card__main-block">
      <div class="management-distribution-card__main">
        {{ displayTotal  }}
      </div>

      <div
          v-if="hasDisplayChange"
          class="management-distribution-card__change"
      >
        <span
            v-if="hasScoreLevelLabel"
            class="management-distribution-card__score-level"
            :class="scoreLevelClass"
        >
          {{ scoreLevelLabel ? (scoreLevelLabel + ":") : '' }}
        </span>

        <span :class="changeClass">
          {{ totalChangeText }}
        </span>

        <span>{{ changeLabel }}</span>
      </div>
    </div>

    <div
        v-if="normalizedLevels.length"
        class="management-distribution-card__legend"
    >
      <div
          v-for="item in normalizedLevels"
          :key="item.key"
          class="management-distribution-card__legend-row"
      >
        <div class="management-distribution-card__legend-name">
          <span
              class="management-distribution-card__legend-mark"
              :style="{ backgroundColor: item.legendColor }"
          ></span>

          <span>{{ item.label }}</span>
        </div>

        <span
            class="management-distribution-card__legend-change"
            :class="getChangeClass(item.change)"
        >
          {{ getChangeText(item.change) }}
        </span>

        <span
            class="management-distribution-card__legend-value"
            :class="{
            'management-distribution-card__legend-value--underlined': item.value !== 0
          }"
        >
          {{ item.value }} чел.
        </span>
      </div>
    </div>

    <div
        v-if="reversedBarSegments.length"
        class="management-distribution-card__bar-block"
    >
      <div
          class="management-distribution-card__bar"
          :style="reversedBarGridStyle"
      >
        <div
            v-for="(item, index) in reversedBarSegments"
            :key="item.key"
            class="management-distribution-card__bar-part"
            :style="getBarPartStyle(index)"
        ></div>
      </div>

      <div
          class="management-distribution-card__bar-values"
          :style="reversedBarGridStyle"
      >
        <span
            v-for="item in reversedBarSegments"
            :key="item.key"
            class="management-distribution-card__bar-value"
        >
          {{ item.value }}
        </span>
      </div>
    </div>
  </div>
</template>

<script>
import { computed } from 'vue'

const GREY_COLOR = '#D1D1D1'

const EI_LEVELS = [
  { from: 50, to: 70, label: 'Элементарный', class: 'low' },
  { from: 70, to: 90, label: 'Низкий', class: 'lowest' },
  { from: 90, to: 110, label: 'Средний', class: 'average' },
  { from: 110, to: 130, label: 'Высокий', class: 'high' },
  { from: 130, to: 150, label: 'Высший', class: 'highest' },
]

export default {
  name: 'ManagementDistributionCard',

  props: {
    total: {
      type: [Number, String, null],
      default: null,
    },
    totalSuffix: {
      type: String,
      default: '',
    },
    levels: {
      type: Array,
      default: () => [],
    },

    score: {
      type: [Number, String, null],
      default: null,
    },
    change: {
      type: [Number, String],
      default: 0,
    },
    changeLabel: {
      type: String,
      default: '',
    },
    indexes: {
      type: Array,
      default: () => [],
    },
  },

  setup(props) {
    const toNumber = (value) => {
      const number = Number(value)
      return Number.isFinite(number) ? number : 0
    }

    const getLevelByValue = (value, list) => {
      return list.find((level) => value >= level.from && value <= level.to) ?? null
    }

    const sourceLevels = computed(() => {
      if (Array.isArray(props.indexes) && props.indexes.length) {
        return props.indexes
      }

      return Array.isArray(props.levels) ? props.levels : []
    })

    const safeTotal = computed(() => {
      if (props.score !== null && props.score !== undefined && props.score !== '') {
        return props.score
      }

      return props.total
    })

    const normalizedLevels = computed(() => {
      return sourceLevels.value.map((item) => {
        const value = toNumber(item.value)
        const change = toNumber(item.change)
        const baseColor = item.color || GREY_COLOR

        return {
          ...item,
          value,
          change,
          legendColor: baseColor,
          color: value === 0 ? GREY_COLOR : baseColor,
        }
      })
    });

    const hasDisplayTotal = computed(() => {
      return safeTotal.value !== null && safeTotal.value !== undefined && safeTotal.value !== ''
    })

    const hasDisplayChange = computed(() => {
      return hasDisplayTotal.value && !!props.changeLabel
    })

    const displayTotal = computed(() => {
      if (!hasDisplayTotal.value) {
        return ''
      }

      if (!safeTotal.value) {
        return '-';
      }

      return `${safeTotal.value}${props.totalSuffix}`
    })

    const safeTotalChange = computed(() => {
      return toNumber(props.change)
    })

    const totalChangeText = computed(() => {
      if (safeTotalChange.value > 0) {
        return `+${safeTotalChange.value}%`
      }

      if (safeTotalChange.value < 0) {
        return `${safeTotalChange.value}%`
      }

      return '0%'
    })

    const changeClass = computed(() => {
      if (safeTotalChange.value > 0) {
        return 'positive-change'
      }

      if (safeTotalChange.value < 0) {
        return 'negative-change'
      }

      return 'neutral-change'
    })

    const scoreLevel = computed(() => {
      if (props.score === null || props.score === undefined || props.score === '') {
        return null
      }

      return getLevelByValue(toNumber(props.score), EI_LEVELS)
    })

    const hasScoreLevelLabel = computed(() => {
      return !!scoreLevel.value?.label
    })

    const scoreLevelLabel = computed(() => {
      return scoreLevel.value?.label ?? ''
    })

    const scoreLevelClass = computed(() => {
      const levelClass = scoreLevel.value?.class

      if (!levelClass) {
        return ''
      }

      return `management-distribution-card__score-level--${levelClass}`
    })

    const levelsTotal = computed(() => {
      return normalizedLevels.value.reduce((sum, item) => sum + item.value, 0)
    })

    const allLevelsAreZero = computed(() => {
      return (
          normalizedLevels.value.length > 0 &&
          normalizedLevels.value.every((item) => item.value === 0)
      )
    })

    const hasChartData = computed(() => {
      const hasLevelValues = normalizedLevels.value.some((item) => item.value > 0)
      const total = toNumber(safeTotal.value)

      return hasLevelValues || total > 0
    })

    const barSegments = computed(() => {
      if (!normalizedLevels.value.length) {
        return []
      }

      if (allLevelsAreZero.value) {
        return normalizedLevels.value.map((item) => ({
          ...item,
          color: GREY_COLOR,
          ratio: 1,
        }))
      }

      if (!levelsTotal.value) {
        return []
      }

      return normalizedLevels.value
          .filter((item) => item.value > 0)
          .map((item) => ({
            ...item,
            ratio: item.value,
          }))
    })

    const reversedBarSegments = computed(() => {
      return [...barSegments.value].reverse()
    })

    const reversedBarGridStyle = computed(() => {
      if (!reversedBarSegments.value.length) {
        return {}
      }

      return {
        gridTemplateColumns: reversedBarSegments.value
            .map((item) => `${item.ratio}fr`)
            .join(' '),
      }
    })

    const getBarPartStyle = (index) => {
      const isFirst = index === 0
      const isLast = index === reversedBarSegments.value.length - 1
      const currentItem = reversedBarSegments.value[index]

      return {
        backgroundColor: currentItem.color,
        borderTopLeftRadius: isFirst ? '999px' : '0',
        borderBottomLeftRadius: isFirst ? '999px' : '0',
        borderTopRightRadius: isLast ? '999px' : '0',
        borderBottomRightRadius: isLast ? '999px' : '0',
      }
    }

    const getChangeText = (value) => {
      if (value > 0) {
        return `↑ ${value}%`
      }

      if (value < 0) {
        return `↓ ${Math.abs(value)}%`
      }

      return '–'
    }

    const getChangeClass = (value) => {
      if (value > 0) {
        return 'positive-change'
      }

      if (value < 0) {
        return 'negative-change'
      }

      return 'neutral-change'
    }

    return {
      hasDisplayTotal,
      hasDisplayChange,
      displayTotal,
      totalChangeText,
      changeClass,
      normalizedLevels,
      reversedBarSegments,
      reversedBarGridStyle,
      hasChartData,
      getBarPartStyle,
      getChangeText,
      getChangeClass,
      hasScoreLevelLabel,
      scoreLevelLabel,
      scoreLevelClass,
    }
  },
}
</script>

<style scoped lang="scss">
.management-distribution-card {
  position: relative;
  width: 100%;
  display: flex;
  flex-direction: column;
  text-align: start;
  height: 100%;
}

.management-distribution-card__main-block {
  margin-bottom: 16px;
}

.management-distribution-card__main {
  color: rgba(0, 0, 0, 0.88);
  font-weight: 600;
  font-size: 24px;
  line-height: 120%;
}

.management-distribution-card__change {
  display: flex;
  gap: 4px;
  margin-top: 4px;
  color: rgba(0, 0, 0, 0.65);
  font-size: 14px;
  line-height: 120%;
}

.management-distribution-card__change span:first-child {
  font-weight: 600;
}

.management-distribution-card__score-level {
  font-weight: 600;
}

.management-distribution-card__score-level--low {
  color: #F55565;
}

.management-distribution-card__score-level--lowest {
  color: #FF9F40;
}

.management-distribution-card__score-level--average {
  color: #DDE00F;
}

.management-distribution-card__score-level--high {
  color: #8BD05B;
}

.management-distribution-card__score-level--highest {
  color: #21AC0C;
}

.management-distribution-card__legend {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.management-distribution-card__legend-row {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(30px, 40px) minmax(30px, 60px);
  align-items: center;
  column-gap: 12px;
  min-width: 0;
  font-size: 14px;
  line-height: 120%;
  color: rgba(0, 0, 0, 0.65);
}

.management-distribution-card__legend-name {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
}

.management-distribution-card__legend-name span:last-child {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.management-distribution-card__legend-mark {
  width: 10px;
  height: 10px;
  border-radius: 2px;
  flex: 0 0 auto;
}

.management-distribution-card__legend-change {
  min-width: 0;
  white-space: nowrap;
  text-align: right;
}

.management-distribution-card__legend-value {
  min-width: 50px;
  text-align: right;
  color: rgba(0, 0, 0, 0.88);
  white-space: nowrap;
}

.management-distribution-card__legend-value--underlined {
  text-decoration: underline;
  cursor: pointer;
}

.management-distribution-card__bar-block {
  margin-top: auto;
}

.management-distribution-card__bar {
  display: grid;
  width: 100%;
  height: 8px;
  margin-top: 12px;
  gap: 2px;
}

.management-distribution-card__bar-part {
  height: 100%;
  min-width: 0;
}

.management-distribution-card__bar-values {
  display: grid;
  width: 100%;
  margin-top: 6px;
  gap: 2px;
  font-size: 13px;
  line-height: 110%;
  color: rgba(0, 0, 0, 0.45);
}

.management-distribution-card__bar-value {
  min-width: 0;
  text-align: center;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.management-distribution-card__empty {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 2;

  padding: 6px 12px;
  border: 1px solid #d9d9d9;
  border-radius: 8px;
  background: #fff;
  font-weight: 400;
  font-size: 12px;
  line-height: 110%;
  text-align: center;
  white-space: nowrap;
}

.positive-change {
  color: var(--main-green);
}

.negative-change {
  color: #F55565;
}

.neutral-change {
  color: rgba(0, 0, 0, 0.45);
}
</style>
